import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthFlow, SignedInPanel } from './modules/auth';
import type { SignedIn } from './modules/auth/AuthFlow';
import { clearSession, lockSession, touchActivity } from './db/auth-store';
import { cloudLogout, revalidateCloudSession } from './db/cloud-auth-store';
import { readActiveView, writeActiveView } from './core/session-cache';
import { resolveNavId } from './core/nav-access';
import { AppLayout, NAV_ITEM_IDS, SecondaryNavPanel } from './components/layout';
import type { NavItemId } from './components/layout';
import { useFleet } from './modules/fleet/use-fleet';
import { DashboardPage } from './modules/dashboard';
import { ReportsPage } from './modules/reports';
import { SettingsPage } from './modules/settings';
import { VehiclesPage } from './modules/fleet/vehicles';
import { SoldiersPage } from './modules/fleet/soldiers';
import { TasksPage, useAssignments, useTasks } from './modules/tasks';
import { ManageUsersPage } from './modules/users';
import { SyncStatusBadge, useCloudSync, useLiveUpdates } from './modules/sync';
import type { User } from './types/user';

/**
 * How long a signed-in device goes before asking the directory whether it is
 * still allowed in. Short enough that revoking somebody takes effect within the
 * shift, long enough that a phone being picked up and put down is not a request
 * per glance.
 */
const REVALIDATE_EVERY_MS = 5 * 60 * 1000;

interface SignedInAppProps {
  auth: SignedIn;
  onLock: () => void;
  onSignOut: () => void;
  /** The operator edited their own details — the whole shell follows them. */
  onProfileUpdated: (user: User) => void;
  /** The directory no longer holds this account, or no longer allows it. */
  onRevoked: () => void;
}

/** The navigated application. Mounted only once a session exists, so the fleet
 *  is never loaded — nor the roster seeded — for a visitor who has not signed
 *  in. */
function SignedInApp({
  auth,
  onLock,
  onSignOut,
  onProfileUpdated,
  onRevoked,
}: SignedInAppProps) {
  // A refresh comes back to the screen the operator was on, not to the home
  // dashboard. `useFleet`/`useTasks` re-read Dexie on mount, so the restored
  // screen is populated from the database rather than from a stale snapshot.
  //
  // The restored view is a screen the *previous* operator on this installation
  // was on, so it is put through the access rules rather than trusted: signing
  // in as a מפקד after a מנהל על lands on the dashboard, not on a settings page
  // this account may not open.
  const [tab, setTab] = useState<NavItemId>(() =>
    resolveNavId(auth.user, readActiveView(NAV_ITEM_IDS)) as NavItemId,
  );
  /** Raised by the dashboard shortcut; the tasks board owns the form itself. */
  const [createRequest, setCreateRequest] = useState(false);
  const { vehicles, soldiers, loading, reload } = useFleet();
  const { tasks, taskTypes, contacts, loading: tasksLoading, reload: reloadTasks } = useTasks();
  // Every convoy in the database, loaded once. It is what turns an assignment
  // into an effective status — a vehicle out on an active task reads "במשימה"
  // on the roster, and its driver with it — so every tab is handed the same
  // derivation rather than each screen inventing its own.
  const { teams, index: assignments, reload: reloadTeams } = useAssignments(tasks);

  useEffect(() => {
    writeActiveView(tab);
  }, [tab]);

  // A role narrowed from the directory while the operator was standing on a
  // screen it no longer opens takes them off it, rather than leaving them on a
  // page whose navigation entry has just disappeared from under them.
  useEffect(() => {
    setTab((current) => resolveNavId(auth.user, current) as NavItemId);
  }, [auth.user]);

  // The unlock window follows real use: mounting counts, and so does every
  // return to the foreground. A pull-to-refresh mid-shift always resumes.
  useEffect(() => {
    touchActivity();
    const stamp = () => touchActivity();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') stamp();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Stamped on the way out as well, so the reload that follows finds it fresh.
    window.addEventListener('pagehide', stamp);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', stamp);
    };
  }, []);

  // ── The session, re-asked of the server that issued it ──────────────
  //
  // A local session is good for thirty days; whether the *account* is still
  // good is the directory's answer and nobody else's. This is what makes the
  // thirty days server-authoritative: disabling somebody in "ניהול משתמשים"
  // reaches their phone on its next check rather than on their next login.
  //
  // A device that cannot reach the server is told nothing and carries on — a
  // dead spot is not grounds for locking somebody out of their own phone.
  const revokedRef = useRef(onRevoked);
  revokedRef.current = onRevoked;
  const profileRef = useRef(onProfileUpdated);
  profileRef.current = onProfileUpdated;

  useEffect(() => {
    let alive = true;
    let lastCheck = 0;

    const revalidate = async () => {
      const now = Date.now();
      if (now - lastCheck < REVALIDATE_EVERY_MS) return;
      lastCheck = now;
      const verdict = await revalidateCloudSession();
      if (!alive) return;
      if (verdict.kind === 'revoked') revokedRef.current();
      // A role changed in the directory follows the operator into the shell,
      // so the navigation they see is the one they are actually entitled to.
      else if (verdict.kind === 'valid') profileRef.current(verdict.user);
    };

    void revalidate();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void revalidate();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  /** After a restore or a reset the whole shell re-reads its data. */
  const reloadAll = useCallback(async () => {
    await Promise.all([reload(), reloadTasks(), reloadTeams()]);
  }, [reload, reloadTasks, reloadTeams]);

  // The cloud mirror. It only ever *adds* — a device with no backend behind it
  // reports "עבודה מקומית" and every screen carries on reading Dexie, which is
  // the same thing the application did before the mirror existed.
  const { state, lastSyncedAt, syncNow } = useCloudSync(true, () => {
    void reloadAll();
  });

  // The other half of "instantly, on every screen": a write in another window on
  // this device is heard on the same frame rather than on the next poll. The
  // data hooks re-read on their own — `emitRemoteStoreChange()` is what the wire
  // raises — so the shell only has to re-read what it holds itself.
  useLiveUpdates(true, () => {
    void reloadAll();
  });

  return (
    <AppLayout
      active={tab}
      onNavigate={setTab}
      user={auth.user}
      status={
        <SyncStatusBadge state={state} lastSyncedAt={lastSyncedAt} onSync={syncNow} />
      }
    >
      {tab === 'home' ? (
        <DashboardPage
          user={auth.user}
          tasks={tasks}
          vehicles={vehicles}
          soldiers={soldiers}
          teams={teams}
          assignments={assignments}
          loading={loading || tasksLoading}
          onNavigate={setTab}
          onCreateTask={() => {
            setCreateRequest(true);
            setTab('tasks');
          }}
        />
      ) : null}

      {tab === 'tasks' ? (
        <TasksPage
          user={auth.user}
          tasks={tasks}
          taskTypes={taskTypes}
          contacts={contacts}
          vehicles={vehicles}
          soldiers={soldiers}
          loading={tasksLoading}
          reload={reloadTasks}
          requestCreate={createRequest}
          onCreateRequestHandled={() => setCreateRequest(false)}
        />
      ) : null}

      {tab === 'vehicles' ? (
        <VehiclesPage
          user={auth.user}
          vehicles={vehicles}
          soldiers={soldiers}
          assignments={assignments}
          loading={loading}
          reload={reload}
        />
      ) : null}

      {tab === 'soldiers' ? (
        <SoldiersPage
          user={auth.user}
          soldiers={soldiers}
          vehicles={vehicles}
          assignments={assignments}
          loading={loading}
          reload={reload}
        />
      ) : null}

      {tab === 'reports' ? (
        <ReportsPage
          user={auth.user}
          tasks={tasks}
          taskTypes={taskTypes}
          contacts={contacts}
          vehicles={vehicles}
          soldiers={soldiers}
          loading={loading || tasksLoading}
        />
      ) : null}

      {tab === 'users' ? <ManageUsersPage user={auth.user} /> : null}

      {tab === 'settings' ? (
        <SettingsPage
          user={auth.user}
          onDataChanged={reloadAll}
          onProfileUpdated={onProfileUpdated}
        />
      ) : null}

      {tab === 'more' ? (
        <div className="mx-auto w-full max-w-2xl space-y-4">
          <SecondaryNavPanel onNavigate={setTab} user={auth.user} />
          <SignedInPanel
            user={auth.user}
            session={auth.session}
            onLock={onLock}
            onSignOut={onSignOut}
            onProfileUpdated={onProfileUpdated}
          />
        </div>
      ) : null}
    </AppLayout>
  );
}

/** Shell — authentication gate in front of the application. */
export default function App() {
  const [auth, setAuth] = useState<SignedIn | null>(null);

  if (!auth) return <AuthFlow onSignedIn={setAuth} />;

  return (
    <SignedInApp
      auth={auth}
      onLock={() => {
        // Deliberate lock: the session stays, the unlock window does not —
        // otherwise the shell would rehydrate straight past the PIN screen.
        lockSession();
        setAuth(null);
      }}
      onSignOut={() => {
        void Promise.all([clearSession(), cloudLogout()]).then(() => setAuth(null));
      }}
      onProfileUpdated={(user) => setAuth((current) => (current ? { ...current, user } : current))}
      // The directory withdrew the account. The local session goes with it —
      // keeping it would leave a revoked operator inside the app until the
      // thirty days ran out.
      onRevoked={() => {
        void clearSession().then(() => setAuth(null));
      }}
    />
  );
}
